import { type Dispatch, type SetStateAction, useMemo, useState } from "react";

import {
  getErrorMessage,
  useGetAsset,
  useMutationRequest,
} from "~/v2/api/hooks";
import { useGetQuotes } from "~/v2/api/hooks/useGetQuotes";
import { useMarketDataFeed } from "~/v2/api/hooks/useMarketDataFeed";
import type { Quote } from "~/v2/api/rest/data";
import { createMultiLegOrder, createOrder } from "~/v2/api/rest/order";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useInvalidateOrderRelatedQueries } from "~/v3-poc/features/orders/useInvalidateOrderRelatedQueries";
import { sideForQuoteSide } from "../../shared/optionLegs/optionLeg";
import type { OrderLeg } from "../../shared/optionLegs/optionLeg.types";
import type { ReviewPresentation } from "../../shared/workspace/workspace.types";
import type {
  OrderReviewSnapshot,
  OrderTicketStep,
  TicketTimeInForce,
  TimeInForceOption,
} from "./OrderTicketWidget.types";
import {
  createMultiLegOrderRequest,
  createOrderReviewSnapshot,
  createSingleOrderRequest,
} from "./OrderTicketWidget.utils";
import {
  deriveTicketMode,
  resolveOrderSymbol,
  type TicketMode,
  type TicketOrderType,
  type TicketSide,
} from "./orderTicket.core";

const TIME_IN_FORCE_OPTIONS: TimeInForceOption[] = [
  { value: "day", label: "Good for day" },
  { value: "gtc", label: "Good till canceled" },
  { value: "ioc", label: "Immediate or cancel" },
  { value: "fok", label: "Fill or kill" },
  { value: "opg", label: "At the open" },
  { value: "cls", label: "At the close" },
];

const MULTILEG_TIME_IN_FORCE_OPTIONS = TIME_IN_FORCE_OPTIONS.filter(
  (option) => option.value === "day" || option.value === "gtc",
);

const OPTION_CONTRACT_MULTIPLIER = 100;

const coerceTimeInForce = (
  timeInForce: TicketTimeInForce,
  options: TimeInForceOption[],
): TicketTimeInForce =>
  options.some((option) => option.value === timeInForce) ? timeInForce : "day";

const getEstimatedCost = ({
  mode,
  quantity,
  orderType,
  limitPrice,
  quote,
}: {
  mode: TicketMode;
  quantity: number;
  orderType: TicketOrderType;
  limitPrice?: number;
  quote?: Quote;
}): number | undefined => {
  if (mode === "multileg" || quantity <= 0) {
    return undefined;
  }
  const price =
    orderType === "limit" ? limitPrice : (quote?.last ?? quote?.ask);
  if (price === undefined || price === null) {
    return undefined;
  }
  return (
    price *
    quantity *
    (mode === "singleOption" ? OPTION_CONTRACT_MULTIPLIER : 1)
  );
};

const getValidationMessage = ({
  mode,
  hasAccount,
  quantity,
  orderType,
  limitPrice,
}: {
  mode: TicketMode;
  hasAccount: boolean;
  quantity: number;
  orderType: TicketOrderType;
  limitPrice?: number;
}): string | undefined => {
  if (mode === "empty") {
    return "Select a symbol.";
  }
  if (!hasAccount) {
    return "No account selected.";
  }
  if (quantity <= 0) {
    return "Enter a quantity.";
  }
  if (orderType === "limit" && (limitPrice ?? 0) <= 0) {
    return "Enter a limit price.";
  }
  return undefined;
};

export interface UseOrderTicketSessionArgs {
  symbol?: string;
  legs: OrderLeg[];
  initialSide?: TicketSide;
  initialQuantity?: number;
  initialOrderType?: TicketOrderType;
  initialLimitPrice?: number;
  onSubmitSuccess?: () => void;
}

export interface UseOrderTicketSessionReturn {
  mode: TicketMode;
  symbol?: string;
  assetName?: string;
  legs: OrderLeg[];
  quote?: Quote;
  side: TicketSide;
  setSide: Dispatch<SetStateAction<TicketSide>>;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  orderType: TicketOrderType;
  setOrderType: Dispatch<SetStateAction<TicketOrderType>>;
  limitPrice?: number;
  setLimitPrice: Dispatch<SetStateAction<number | undefined>>;
  timeInForce: TicketTimeInForce;
  setTimeInForce: Dispatch<SetStateAction<TicketTimeInForce>>;
  timeInForceOptions: TimeInForceOption[];
  estimatedCost?: number;
  validationMessage?: string;
  step: OrderTicketStep;
  reviewSnapshot?: OrderReviewSnapshot;
  submitError?: string;
  submittedOrderId?: string;
  reviewEnabled: boolean;
  reviewPresentation: ReviewPresentation;
  setReviewEnabled: (value: boolean) => void;
  setReviewPresentation: (value: ReviewPresentation) => void;
  onPrimary: () => void;
  onBackToEdit: () => void;
  onConfirm: () => void;
  onNewOrder: () => void;
  canReview: boolean;
  isSubmitting: boolean;
}

export const useOrderTicketSession = ({
  symbol,
  legs,
  initialSide,
  initialQuantity,
  initialOrderType,
  initialLimitPrice,
  onSubmitSuccess,
}: UseOrderTicketSessionArgs): UseOrderTicketSessionReturn => {
  const { currentAccount, product } = useAccountContext();
  const invalidateOrderRelatedQueries = useInvalidateOrderRelatedQueries();
  const { latestFeed } = useMarketDataFeed();
  const [side, setSide] = useState<TicketSide>(initialSide ?? "buy");
  const [quantity, setQuantity] = useState(initialQuantity ?? 1);
  const [orderType, setOrderType] = useState<TicketOrderType>(
    initialOrderType ?? "market",
  );
  const [limitPrice, setLimitPrice] = useState(initialLimitPrice);
  const [timeInForce, setTimeInForce] = useState<TicketTimeInForce>("day");
  const [step, setStep] = useState<OrderTicketStep>("editing");
  const [reviewSnapshot, setReviewSnapshot] = useState<OrderReviewSnapshot>();
  const [submitError, setSubmitError] = useState<string>();
  const [submittedOrderId, setSubmittedOrderId] = useState<string>();

  const singleLeg = legs.length === 1 ? legs[0] : undefined;
  const legSeedKey = singleLeg
    ? `1:${singleLeg.contract.symbol}:${singleLeg.quoteSide}`
    : `n:${legs.length}`;
  const [seededLegKey, setSeededLegKey] = useState<string | undefined>(
    undefined,
  );
  if (legSeedKey !== seededLegKey) {
    setSeededLegKey(legSeedKey);
    if (singleLeg) {
      setSide(sideForQuoteSide(singleLeg.quoteSide));
      if (singleLeg.price !== undefined) {
        setOrderType("limit");
        setLimitPrice(singleLeg.price);
      }
    } else if (legs.length >= 2) {
      setOrderType("market");
      setLimitPrice(undefined);
    }
  }

  const mode = useMemo(() => deriveTicketMode(symbol, legs), [legs, symbol]);
  const orderSymbol = resolveOrderSymbol(mode, symbol, legs);
  const { quotes } = useGetQuotes({
    symbols: orderSymbol ? [orderSymbol] : [],
    feed: latestFeed,
    options: { enabled: !!orderSymbol },
  });
  const quote = quotes?.find((candidate) => candidate.symbol === orderSymbol);

  const { asset } = useGetAsset({
    symbol: symbol ?? "",
    options: { enabled: mode === "equity" && !!symbol },
  });
  const assetName = mode === "equity" ? asset?.name : undefined;
  const timeInForceOptions =
    mode === "multileg"
      ? MULTILEG_TIME_IN_FORCE_OPTIONS
      : TIME_IN_FORCE_OPTIONS;
  const effectiveTimeInForce = coerceTimeInForce(
    timeInForce,
    timeInForceOptions,
  );
  const estimatedCost = getEstimatedCost({
    mode,
    quantity,
    orderType,
    limitPrice,
    quote,
  });
  const validationMessage = getValidationMessage({
    mode,
    hasAccount: !!currentAccount?.id,
    quantity,
    orderType,
    limitPrice,
  });

  type CreateOrderResponse = Awaited<ReturnType<typeof createOrder>>;
  const mutation = useMutationRequest<CreateOrderResponse, OrderReviewSnapshot>(
    async (snapshot) => {
      if (!currentAccount?.id) {
        throw new Error("No account selected");
      }
      const tradeProduct = product === "live" ? "live" : "paper";
      if (snapshot.mode === "multileg") {
        return createMultiLegOrder(
          currentAccount.id,
          createMultiLegOrderRequest(snapshot),
          tradeProduct,
        );
      }
      return createOrder(
        currentAccount.id,
        createSingleOrderRequest(snapshot),
        tradeProduct,
      );
    },
    {
      onSuccess: (response) => {
        setSubmittedOrderId(response.data.id);
        setSubmitError(undefined);
        setStep("success");
        invalidateOrderRelatedQueries();
        onSubmitSuccess?.();
      },
      onError: (error) => {
        setSubmitError(getErrorMessage(error));
        setStep("reviewing");
      },
    },
  );

  const canReview = !validationMessage && !mutation.isPending;

  const onPrimary = () => {
    const displaySymbol =
      symbol ?? orderSymbol ?? legs[0]?.contract.symbol ?? "Multi-leg order";
    if (validationMessage || mode === "empty") {
      return;
    }
    const snapshot = createOrderReviewSnapshot({
      mode,
      displaySymbol,
      orderSymbol,
      side,
      quantity,
      orderType,
      limitPrice,
      timeInForce: effectiveTimeInForce,
      estimatedCost,
      legs,
    });
    setReviewSnapshot(snapshot);
    setSubmitError(undefined);
    if (reviewEnabled) {
      setStep("reviewing");
      return;
    }
    setStep("submitting");
    mutation.mutate(snapshot);
  };

  const [reviewEnabled, setReviewEnabled] = useState(true);
  const [reviewPresentation, setReviewPresentation] =
    useState<ReviewPresentation>("inline");
  const onBackToEdit = () => {
    if (step !== "submitting") {
      setSubmitError(undefined);
      setStep("editing");
    }
  };
  const onConfirm = () => {
    if (!reviewSnapshot || step !== "reviewing") {
      return;
    }
    setSubmitError(undefined);
    setStep("submitting");
    mutation.mutate(reviewSnapshot);
  };
  const onNewOrder = () => {
    setSide("buy");
    setQuantity(1);
    setOrderType("market");
    setLimitPrice(undefined);
    setTimeInForce("day");
    setReviewSnapshot(undefined);
    setSubmitError(undefined);
    setSubmittedOrderId(undefined);
    setStep("editing");
  };

  return {
    mode,
    symbol,
    assetName,
    legs,
    quote,
    side,
    setSide,
    quantity,
    setQuantity,
    orderType,
    setOrderType,
    limitPrice,
    setLimitPrice,
    timeInForce: effectiveTimeInForce,
    setTimeInForce,
    timeInForceOptions,
    estimatedCost,
    validationMessage,
    step,
    reviewSnapshot,
    submitError,
    submittedOrderId,
    reviewEnabled,
    reviewPresentation,
    setReviewEnabled,
    setReviewPresentation,
    onPrimary,
    onBackToEdit,
    onConfirm,
    onNewOrder,
    canReview,
    isSubmitting: mutation.isPending,
  };
};
